import { useCallback, useEffect, useRef, useState } from "react";
import type { BCCADMINBaseModelsNetworkStatusResponse } from "../API/Models";
import { client } from "../Clients";

/**
 * Reachability of the deployed instances (public sites, API, …) over the last N hours, via
 * GET /api/network-status : powers the Network Status page's uptime cards and timeline. The server
 * does the probing and keeps the history; this hook only reads. `loading` = initial fetch,
 * `isRefreshing` = manual refresh() with the stale data kept visible.
 */
export const useNetworkStatusData = (hours = 12) => {
	const [data, setData] = useState<BCCADMINBaseModelsNetworkStatusResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const isMountedRef = useRef(true);

	const fetchStatus = useCallback(
		async (manual = false) => {
			if (manual) {
				setRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);
			try {
				const response = await client.networkStatus.apiNetworkStatusGet(hours);
				if (!isMountedRef.current) return;
				setData(response.data ?? null);
			} catch (caughtError) {
				console.error(caughtError);
				if (!isMountedRef.current) return;
				setError("Unable to load network status. Please try again.");
			} finally {
				if (!isMountedRef.current) return;
				if (manual) {
					setRefreshing(false);
				} else {
					setLoading(false);
				}
			}
		},
		[hours]
	);

	useEffect(() => {
		isMountedRef.current = true;
		fetchStatus();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchStatus]);

	return {
		data,
		loading,
		error,
		isRefreshing: refreshing,
		refresh: () => fetchStatus(true)
	};
};

export default useNetworkStatusData;

